import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { detailPending, detailSettled } from "helpers/detailRequest";
import apiAxios, { apiAxiosUpload } from "helpers/apiAxios";
import { toast } from "react-toastify";
import { addRejectedMatcher, handleAsyncThunkError } from "redux/reduxHelpers";

import {
  initialState,
  createInitialState,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handlePayload
} from "helpers/reducerHelpers";

const projectUrl = "/crm/projects"

// `signal` is threaded through to axios so that aborting the thunk actually
// cancels the HTTP request. Without it a superseded search kept downloading its
// full ~90 KB payload: typing "24774" left five overlapping responses in flight,
// and whichever finished last won — so a slow "2" could land after "24774" and
// replace the right answer with the wrong one.
export const getAllProjects = createAsyncThunk(
  "Project/getAllProjects",
  async (params, { rejectWithValue, signal }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}`, { params, signal } )
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get projects'));
    }
  }
)

export const getProject = createAsyncThunk(
  "Project/getProject",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/${projectId}`)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get project'));
    }
  }
)

export const createProject = createAsyncThunk(
  "Project/createProject",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(projectUrl, {project: data})
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create project'))
    }
  }
)

export const updateProject = createAsyncThunk(
  "Project/updateProject",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${projectUrl}/${projectId}`, {project: data})
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update project'));
    }
  }
)

export const deleteProject = createAsyncThunk(
  "Project/deleteProject",
  async (project, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${projectUrl}/${project.id}`);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete project'));
    }
  }
)

export const convertQuoteNumber = createAsyncThunk(
  "Project/convertQuoteNumber",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/convert_quotation`)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to convert quote number'));
    }
  }
)

export const changeStatusProject = createAsyncThunk(
  "Project/changeStatusProject",
  async ({projectId, data}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/change_status`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to change project status'));
    }
  }
)

export const updateQuotation= createAsyncThunk(
  "Project/updateQuotation",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${projectUrl}/${projectId}/update_quotation`, {project: data})
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update quotation'));
    }
  }
)

const EXPORT_TIMEOUT_MS = 300000;

export const exportProjects = createAsyncThunk(
  "Project/exportProjects",
  async (params) => {
    // Pagination does not apply to the sheet; the full list can take well over
    // the default 30s request timeout to build.
    const rest = Object.fromEntries(
      Object.entries(params).filter(([k]) => k !== 'page' && k !== 'per_page')
    )
    const response = await apiAxios.get(`${projectUrl}/export`, {
      params: rest, responseType: 'blob', timeout: EXPORT_TIMEOUT_MS,
    })

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `projects-${params?.export_status}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
)

export const getActivityLog = createAsyncThunk(
  "Project/getActivityLog",
  async ({ projectId, params } ) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/${projectId}/activity_log`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getQuotationBuildings = createAsyncThunk(
  "Project/getQuotationBuildings",
  async ({ projectId, params }, { rejectWithValue } ) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/${projectId}/load_quotation_buildings`, { params })
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get quotation buildings'));
    }
  }
)

export const assignQuotationToSSE = createAsyncThunk(
  "Project/assignQuotationToSSE",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const isFormData = data instanceof FormData;
      const response = await apiAxios.post(
        `${projectUrl}/${projectId}/assign_to_sse`,
        data,
        isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}
      )
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to assign quotation to SSE'));
    }
  }
)

export const getNewPCVProjects = createAsyncThunk(
  "Project/getNewPCVProjects",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/new_pcvs`, { params } )
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get new PCV projects'));
    }
  }
)

export const getQuotationProjects = createAsyncThunk(
  "Project/getQuotationProjects",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/quotations`, { params } )
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get quotation projects'));
    }
  }
)

export const getNegotiationProjects = createAsyncThunk(
  "Project/getNegotiationProjects",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/negotiations`, { params } )
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get negotiation projects'));
    }
  }
)

export const getJobProjects = createAsyncThunk(
  "Project/getJobProjects",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/jobs`, { params } )
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get job projects'));
    }
  }
)

export const getLostProjects = createAsyncThunk(
  "Project/getLostProjects",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/losts`, { params } )
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get lost projects'));
    }
  }
)

export const requestEstimateBuildings = createAsyncThunk(
  "Quotation/requestEstimateBuildings",
  async ({projectId, params}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/request_estimate_buildings`, { estimate: params } );
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to request estimate buildings'));
    }
  }
)

export const scheduleEstimateBuildings = createAsyncThunk(
  "Quotation/scheduleEstimateBuildings",
  async ({projectId, params}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/schedule_estimate_buildings`, { estimate: params } );
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to schedule estimate buildings'));
    }
  }
)

export const releaseEstimateBuildings = createAsyncThunk(
  "Quotation/releaseEstimateBuildings",
  async ({projectId, data}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/release_estimate_buildings`, data, {
        headers: { 'Content-Type': 'multipart/form-data'}
      })
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to release estimate buildings'));
    }
  }
)

export const changeSalesUnitProject = createAsyncThunk(
  "Project/changeSalesUnitProject",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/change_sales_unit`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to change sales unit project'));
    }
  }
)

export const createFolderProject = createAsyncThunk(
  "Project/createFolderProject",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/create_folder`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create folder project'));
    }
  }
)

export const getDocuments = createAsyncThunk(
  "Project/getDocuments",
  async ({projectId, params}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/${projectId}/list_documents`, {params})
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get documents'));
    }
  }
)

export const uploadDocument = createAsyncThunk(
  "Project/uploadDocument",
  async ({ projectId, formData }, { rejectWithValue }) => {
    try {
      const response = await apiAxiosUpload.post(`${projectUrl}/${projectId}/upload_document`, formData)
      return response.data;
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to upload document'))
    }
  }
)

export const removeDocument = createAsyncThunk(
  "Project/removeDocument",
  async ( { projectId, assetId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/remove_document`, { asset_id: assetId });
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to remove document'))
    }
  }
)

export const insertLink = createAsyncThunk(
  "Project/insertLink",
  async ({ projectId, name, linkUrl, assetId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/insert_link`, {
        name,
        link_url: linkUrl,
        ...(assetId ? { asset_id: assetId } : {}),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to insert link'));
    }
  }
);

export const getOplaDocuments = createAsyncThunk(
  "Project/getOplaDocuments",
  async ({ projectId, params }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/${projectId}/list_opla_documents`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get Opla CRM documents'));
    }
  }
);

export const getProjectContracts = createAsyncThunk(
  "Project/getProjectContracts",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/${projectId}/load_project_contracts`);
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get project contracts'))
    }
  }
)

export const getSalesPipelines = createAsyncThunk(
  'Project/getSalesPipelines',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/sales_pipelines`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to load sales pipelines'));
    }
  }
);

export const syncProjectToOplaCrm = createAsyncThunk(
  "Project/syncProjectToOplaCrm",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/sync_sales_process_opla`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to sync project to Opla CRM'));
    }
  }
)

export const syncFromOplaCrm = createAsyncThunk(
  "Project/syncFromOplaCrm",
  async ({ projectId, syncTypes }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/sync_from_opla`, { sync_types: syncTypes });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to sync from Opla CRM'));
    }
  }
);

// Fetch pipeline stats — optionally with the same filter params as the list/kanban
// so Pipeline Overview always stays in sync with active filters.
export const getPipelineStats = createAsyncThunk(
  'Project/getPipelineStats',
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/pipeline_stats`, { params, signal });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to load pipeline stats'));
    }
  }
);

export const lostReasonProject = createAsyncThunk(
  "Project/lostReasonProject",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/lost_reasons`)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get lost reasons'));
    }
  }
)

export const createLostReason = createAsyncThunk(
  "Project/createLostReason",
  async ({ value }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/create_lost_reason`, { value });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create lost reason'));
    }
  }
)

export const closeLostProject = createAsyncThunk(
  "EnterJob/closeLostProject",
  async ({projectId, data}) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/close_lost`, data);
      return response.data
      
    } catch (error) {
      throw new Error(error.message)
    }
  }
)

// Undo the last Close lost. The backend restores the statuses it snapshotted and
// clears the snapshot, so the button disappears afterwards.
export const rollbackLostProject = createAsyncThunk(
  "Project/rollbackLostProject",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/rollback_lost`);
      return response.data
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to reopen the project'))
    }
  }
)

// Fill in / extend the reason of a project that is already Lost. Returns the
// refreshed project, so the Lost stage re-renders with the new reason.
export const updateLostReason = createAsyncThunk(
  "Project/updateLostReason",
  async ({ projectId, reason, reasonId, details }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.patch(`${projectUrl}/${projectId}/update_lost_reason`,
        { reason, reason_id: reasonId, details });
      return response.data
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update the lost reason'))
    }
  }
)

export const quotationRequestRevisePIFPPD = createAsyncThunk(
  "Project/quotationRequestRevisePIFPPD",
  async ({ projectId, data } ) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/quotation_request_revise_buildings`, data)
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const quotationRejectRevisePIFPPD = createAsyncThunk(
  "Project/quotationRejectRevisePIFPPD",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/quotation_reject_revise_buildings`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to reject revise PIFPPD'));
    }
  }
)

export const quotationAcceptRevisePIFPPD = createAsyncThunk(
  "Project/quotationAcceptRevisePIFPPD",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/quotation_accept_revise_buildings`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to accept revise PIFPPD'));
    }
  }
)

const projectSlice = createSlice({
  name: "crm_project",
  initialState: {
    ...initialState,
    newPCVData: createInitialState(),
    quotationData: createInitialState(),
    negotiationData: createInitialState(),
    jobData: createInitialState(),
    lostData: createInitialState(),
    salesPipelines: [],
    salesPipelinesLoading: false,
    pipelineStats:        null,
    pipelineStatsLoading: false,
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllProjects.fulfilled, (state, action) => handleFulfilled(state, action, "allData"))
      .addCase(getNewPCVProjects.fulfilled, (state, action) => handleFulfilled(state, action, "newPCVData"))
      .addCase(getQuotationProjects.fulfilled, (state, action) => handleFulfilled(state, action, "quotationData"))
      .addCase(getNegotiationProjects.fulfilled, (state, action) => handleFulfilled(state, action, "negotiationData"))
      .addCase(getJobProjects.fulfilled, (state, action) => handleFulfilled(state, action, "jobData"))
      .addCase(getLostProjects.fulfilled, (state, action) => handleFulfilled(state, action, "lostData"))
      .addCase(getActivityLog.fulfilled, (state, action) => handleFulfilled(state, action, "activityLogData"))
      .addCase(getPipelineStats.pending,   (s) => { s.pipelineStatsLoading = true; })
      .addCase(getPipelineStats.fulfilled, (s, a) => { s.pipelineStatsLoading = false; s.pipelineStats = a.payload; })
      .addCase(getPipelineStats.rejected,  (s) => { s.pipelineStatsLoading = false; })
      
      .addCase(getProject.pending, detailPending())
      .addCase(getProject.fulfilled, detailSettled((state, action) => handlePayload(state, action)))
      .addCase(getProject.rejected, detailSettled(null))
      .addCase(createProject.fulfilled, (state, action) => handlePayload(state, action, "create"))
      .addCase(updateProject.fulfilled, (state, action) =>  handlePayload(state, action))
      .addCase(deleteProject.fulfilled, (state, action) =>  handlePayload(state, action, "delete"))
      .addCase(convertQuoteNumber.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(updateQuotation.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(changeStatusProject.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(exportProjects.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportProjects.fulfilled, (state) => {
        state.exporting = false
        toast.success("Project has been exported successfully.")
      })
      .addCase(exportProjects.rejected, (state) => {
        state.exporting = false
      })
      .addCase(assignQuotationToSSE.fulfilled, (state, action) => handlePayload(state, action))

      .addCase(requestEstimateBuildings.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(scheduleEstimateBuildings.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(releaseEstimateBuildings.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(changeSalesUnitProject.fulfilled, (state, action) =>  handlePayload(state, action))
      .addCase(closeLostProject.fulfilled, (state, action) =>  handlePayload(state, action))
      .addCase(rollbackLostProject.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(updateLostReason.fulfilled, (state, action) => handlePayload(state, action))

      .addCase(quotationRequestRevisePIFPPD.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(quotationAcceptRevisePIFPPD.fulfilled, (state, action) => handlePayload(state, action))
      .addCase(quotationRejectRevisePIFPPD.fulfilled, (state, action) => handlePayload(state, action))

      .addCase(getSalesPipelines.pending, (state) => { state.salesPipelinesLoading = true; })
      .addCase(getSalesPipelines.fulfilled, (state, action) => {
        state.salesPipelinesLoading = false;
        state.salesPipelines = action.payload?.data || [];
      })
      .addCase(getSalesPipelines.rejected, (state) => { state.salesPipelinesLoading = false; })

      // Add standard rejected action matcher using shared helper
      addRejectedMatcher(builder, ["EnterJob", "Project", "Quotation"]);
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = projectSlice.actions

export default projectSlice.reducer